/**
 * Persists risk reduction measure list filters in sessionStorage per Machinery
 * Directive compliance record.
 */

import type {
  RiskReductionMeasureDependsOnControlSystemFilter,
  RiskReductionMeasureLinkedToHazardsFilter,
  RiskReductionMeasureListFilters,
  RiskReductionMeasurePlrFilter,
  RiskReductionMeasureSafetyFunctionFilter,
  RiskReductionMeasureSourceFilter,
  RiskReductionMeasureTypeFilter,
} from './riskReductionMeasureListFilters'

const STORAGE_KEY_PREFIX =
  'machineryDirective.riskReductionMeasures.listFilters:' as const

const MEASURE_TYPE_VALUES = new Set<RiskReductionMeasureTypeFilter>([
  'all',
  'ISD',
  'SCP',
  'PPE',
  'OI',
])

const SOURCE_VALUES = new Set<RiskReductionMeasureSourceFilter>([
  'all',
  'USER',
  'STANDARD',
])

const DEPENDS_ON_CS_VALUES =
  new Set<RiskReductionMeasureDependsOnControlSystemFilter>(['all', 'yes', 'no'])

const SAFETY_FUNCTION_VALUES = new Set<RiskReductionMeasureSafetyFunctionFilter>(
  ['all', 'assigned', 'not_assigned']
)

const PLR_VALUES = new Set<RiskReductionMeasurePlrFilter>([
  'all',
  'A',
  'B',
  'C',
  'D',
  'E',
  'none',
])

const LINKED_HAZARDS_VALUES = new Set<RiskReductionMeasureLinkedToHazardsFilter>(
  ['all', 'linked', 'not_linked']
)

function getStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') {
      return null
    }
    return window.sessionStorage
  } catch {
    return null
  }
}

function makeKey(machineryDirectiveComplianceId: string): string {
  return `${STORAGE_KEY_PREFIX}${machineryDirectiveComplianceId}`
}

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === 'string')
  )
}

function parseRiskReductionMeasureListFilters(
  raw: string
): RiskReductionMeasureListFilters | null {
  try {
    const parsed: unknown = JSON.parse(raw)
    if (parsed === null || typeof parsed !== 'object') {
      return null
    }

    const record = parsed as Record<string, unknown>
    const measureType = record.measureType
    const source = record.source
    const ehsrClauseIds = record.ehsrClauseIds
    const includeNoEhsr = record.includeNoEhsr
    const dependsOnControlSystem = record.dependsOnControlSystem
    const safetyFunction = record.safetyFunction
    const plr = record.plr
    const linkedToHazards = record.linkedToHazards

    if (
      typeof measureType !== 'string' ||
      !MEASURE_TYPE_VALUES.has(measureType as RiskReductionMeasureTypeFilter)
    ) {
      return null
    }

    if (
      typeof source !== 'string' ||
      !SOURCE_VALUES.has(source as RiskReductionMeasureSourceFilter)
    ) {
      return null
    }

    if (!isStringArray(ehsrClauseIds)) {
      return null
    }

    if (typeof includeNoEhsr !== 'boolean') {
      return null
    }

    if (
      typeof dependsOnControlSystem !== 'string' ||
      !DEPENDS_ON_CS_VALUES.has(
        dependsOnControlSystem as RiskReductionMeasureDependsOnControlSystemFilter
      )
    ) {
      return null
    }

    if (
      typeof safetyFunction !== 'string' ||
      !SAFETY_FUNCTION_VALUES.has(
        safetyFunction as RiskReductionMeasureSafetyFunctionFilter
      )
    ) {
      return null
    }

    if (
      typeof plr !== 'string' ||
      !PLR_VALUES.has(plr as RiskReductionMeasurePlrFilter)
    ) {
      return null
    }

    if (
      typeof linkedToHazards !== 'string' ||
      !LINKED_HAZARDS_VALUES.has(
        linkedToHazards as RiskReductionMeasureLinkedToHazardsFilter
      )
    ) {
      return null
    }

    return {
      measureType: measureType as RiskReductionMeasureTypeFilter,
      source: source as RiskReductionMeasureSourceFilter,
      ehsrClauseIds,
      includeNoEhsr,
      dependsOnControlSystem:
        dependsOnControlSystem as RiskReductionMeasureDependsOnControlSystemFilter,
      safetyFunction: safetyFunction as RiskReductionMeasureSafetyFunctionFilter,
      plr: plr as RiskReductionMeasurePlrFilter,
      linkedToHazards: linkedToHazards as RiskReductionMeasureLinkedToHazardsFilter,
    }
  } catch {
    return null
  }
}

export function saveRiskReductionMeasureListFilters(
  machineryDirectiveComplianceId: string,
  filters: RiskReductionMeasureListFilters
): void {
  const storage = getStorage()
  if (storage === null || machineryDirectiveComplianceId.length === 0) {
    return
  }

  try {
    storage.setItem(
      makeKey(machineryDirectiveComplianceId),
      JSON.stringify(filters)
    )
  } catch {
    // Quota, private mode, or disabled storage — ignore.
  }
}

export function readRiskReductionMeasureListFilters(
  machineryDirectiveComplianceId: string
): RiskReductionMeasureListFilters | null {
  const storage = getStorage()
  if (storage === null || machineryDirectiveComplianceId.length === 0) {
    return null
  }

  try {
    const raw = storage.getItem(makeKey(machineryDirectiveComplianceId))
    if (raw === null) {
      return null
    }
    return parseRiskReductionMeasureListFilters(raw)
  } catch {
    return null
  }
}

/** Test-only helper to read the raw storage key for a compliance record. */
export function __getRiskReductionMeasureListFiltersStorageKeyForTests(
  machineryDirectiveComplianceId: string
): string {
  return makeKey(machineryDirectiveComplianceId)
}
